import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faFacebookF,
  faInstagram,
  faTiktok,
  faWhatsapp,
} from "@fortawesome/free-brands-svg-icons";
import { faPhone, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { HiGlobeAlt, HiChevronDown } from "react-icons/hi";
import { HiXMark, HiBars3 } from "react-icons/hi2";

const navLinks = [
  { label: "Home", href: "#home", sw: "Nyumbani" },
  { label: "About Us", href: "#about", sw: "Kuhusu Sisi" },
  { label: "Our Services", href: "#services", sw: "Huduma Zetu" },
  { label: "FAQ", href: "#faq", sw: "Maswali" },
];

const socialLinks = [
  {
    icon: faFacebookF,
    href: "https://www.facebook.com/mulsolgroups",
    label: "Facebook",
  },
  {
    icon: faInstagram,
    href: "https://www.instagram.com/mulsolgroup",
    label: "Instagram",
  },
  {
    icon: faTiktok,
    href: "https://www.tiktok.com/@mulsolgroup",
    label: "TikTok",
  },
  { icon: faWhatsapp, href: "https://wa.me/255713343851", label: "WhatsApp" },
];

const LandingHeader = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [language, setLanguage] = useState("en");
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const getText = (enText, swText) => (language === "en" ? enText : swText);

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    setLanguageDropdownOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = () => setLanguageDropdownOpen(false);
    if (languageDropdownOpen) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => document.removeEventListener("click", handleClickOutside);
  }, [languageDropdownOpen]);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-md border-b-[3px] border-orange-600"
          : "bg-white border-b border-gray-200"
      }`}
    >
      {/* ── TOP BAR ── */}
      <div className="w-full bg-white text-gray-900 py-2 text-lg border-b-[2px] border-gray-300">
        <div className="w-full px-4 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between">
            {/* LEFT — Social Icons */}
            <div className="flex items-center gap-1">
              {socialLinks.map(({ icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-7 h-7 flex items-center justify-center rounded-md hover:text-blue-900 text-gray-700"
                >
                  <FontAwesomeIcon icon={icon} className="text-lg" />
                </a>
              ))}
            </div>

            {/* RIGHT — Contact */}
            <div className="flex items-center gap-4">
              <a
                href="tel:+255713343851"
                className="hidden sm:flex items-center gap-2 font-bold text-gray-800"
              >
                <FontAwesomeIcon icon={faPhone} className="text-orange-600" />
                <span className="text-lg">+255 713 343 851</span>
              </a>

              <a
                href="mailto:mulsolgroup@gmail.com"
                className="hidden sm:flex items-center gap-2 font-bold text-gray-800"
              >
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className="text-orange-600"
                />
                <span className="text-lg">mulsolgroup@gmail.com</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── MAIN HEADER ── */}
      <div className="w-full px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-[72px] relative">
          {/* LEFT — Logo */}
          <Link to="/" className="flex items-center shrink-0">
            <span className="text-2xl font-black text-blue-800">Shop</span>
            <span className="text-2xl font-black text-orange-600">
              Kiganjani
            </span>
          </Link>

          {/* CENTER — NAV (hidden on tablet, visible on desktop) */}
          <nav className="hidden lg:flex items-center gap-2 absolute left-1/2 -translate-x-1/2">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="relative px-4 py-2 text-base font-bold text-gray-700 hover:text-blue-600 group"
              >
                {getText(link.label, link.sw)}
                <span className="absolute bottom-0 left-4 right-4 h-[2px] bg-blue-800 scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
              </a>
            ))}
          </nav>

          {/* RIGHT — Actions */}
          <div className="flex items-center gap-3">
            {/* Language */}
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)}
                className="flex items-center gap-1 px-3 py-2 text-sm font-bold border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <HiGlobeAlt className="w-4 h-4 text-blue-700" />
                {language === "en" ? "EN" : "SW"}
                <HiChevronDown className="w-4 h-4" />
              </button>

              {languageDropdownOpen && (
                <div className="absolute right-0 mt-2 w-32 bg-white border rounded-lg shadow-lg">
                  {["en", "sw"].map((code) => (
                    <button
                      key={code}
                      onClick={() => handleLanguageChange(code)}
                      className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                    >
                      {code === "en" ? "English" : "Kiswahili"}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="w-px h-8 bg-gray-400 shrink-0" />

            {/* Buttons - Now visible on tablet (md) and desktop (lg) */}
            <div className="hidden md:flex items-center gap-2">
              <Link to="/Login">
                <button className="px-4 py-2 font-bold text-gray-700 hover:text-blue-700">
                  {getText("Log In", "Ingia")}
                </button>
              </Link>

              <Link to="/">
                <button className="px-5 py-2.5 bg-blue-700 text-white rounded-lg font-bold hover:bg-blue-800 flex items-center gap-2">
                  {getText("Get Started", "Anza")}
                </button>
              </Link>
            </div>

            {/* Mobile Toggle - visible only on mobile (<768px) */}
            <button
              className="md:hidden"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <HiXMark size={24} /> : <HiBars3 size={24} />}
            </button>
          </div>
        </div>

        {/* MOBILE MENU - visible only when mobileOpen is true */}
        {mobileOpen && (
          <div className="md:hidden mt-4 pb-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="block px-4 py-3 font-bold text-gray-700 border-b border-gray-100"
                onClick={() => setMobileOpen(false)}
              >
                {getText(link.label, link.sw)}
              </a>
            ))}
            {/* Mobile Auth Buttons */}
            <div className="flex flex-col gap-2 mt-4 px-4">
              <Link to="/Login" onClick={() => setMobileOpen(false)}>
                <button className="w-full py-2.5 font-bold text-gray-700 border border-gray-300 rounded-lg">
                  {getText("Log In", "Ingia")}
                </button>
              </Link>
              <Link to="/" onClick={() => setMobileOpen(false)}>
                <button className="w-full py-2.5 bg-blue-700 text-white rounded-lg font-bold">
                  {getText("Get Started", "Anza")}
                </button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default LandingHeader;
